/**
 * Global logging utility for ThreeSixty frontend
 * 
 * This provides consistent logging across the application with environment-aware behavior.
 * In development: Full console output
 * In production: Suppresses console logs, only critical errors are recorded
 */

// Determine environment
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
const LOG_LEVEL = process.env.VUE_APP_LOG_LEVEL || (IS_DEVELOPMENT ? 'debug' : 'error');

// Log levels and their priorities
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4
};

// Get numeric priority of current log level
const currentLogLevelPriority = LOG_LEVELS[LOG_LEVEL] || LOG_LEVELS.error;

// Utility to check if a log level should be shown
const shouldLog = (level) => {
  const levelPriority = LOG_LEVELS[level] || LOG_LEVELS.error;
  return levelPriority >= currentLogLevelPriority;
};

/**
 * Logger object with methods for different log levels
 */
const logger = {
  /**
   * Debug level logging - only in development
   */
  debug: (message, ...args) => {
    if (shouldLog('debug')) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  },

  /**
   * Info level logging
   */
  info: (message, ...args) => {
    if (shouldLog('info')) {
      console.info(`[INFO] ${message}`, ...args);
    }
  },

  /**
   * Warning level logging
   */
  warn: (message, ...args) => {
    if (shouldLog('warn')) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  },

  /**
   * Error level logging - always shown in dev, controlled in production
   */
  error: (message, ...args) => {
    if (shouldLog('error')) {
      console.error(`[ERROR] ${message}`, ...args);
    }
    // Here you could add error reporting service integration
  }
};

export default logger; 